import { useTheme } from '@emotion/react'
import {
    BreachEvent,
    BreachScreenContext,
    ConfigurationContext,
    DataLayerEventName,
    getEmailVerificationPath,
    PublicationType,
    useSubscribeWithGoogle,
    ViewSubscriptionItem,
    SwgMetaInfo,
    click_origin_breach_screen,
    useFeatureValue,
    useFeature,
} from '@news-mono/web-common'
import { SectionMetaInfoDTO } from '@west-australian-newspapers/publication-types'
import * as CryptoJS from 'crypto-js'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
    DEFAULT_CART_QUANTITY,
    getRrmeCampaignOfferValues,
    isRrmeCampaignOfferValid,
    LinkContext,
    RrmeCampaignOffer,
    TEACHERS_OFFER,
    UNIVERSITY_OFFER,
    useAuthActions,
} from '../..'
import { getAttribution } from '../../errors/ErrorPage'
import {
    EVERYDAY_DIGITAL,
    packageOfferDetails,
    rrmeCampaignOfferDetails,
    useUpdateBreachWallContext,
    WEEKLY_DIGITAL_RATE_CODE,
} from '../../user-registration/breach/common'
import {
    StyledHelpMessage,
    StyledTerms2022,
    StyledTextLink,
} from '../../user-registration/breach/components/BreachWallContent/BreachWallContent.styled'
import {
    StyledSubscribeWithGoogleButton,
    StyledSubscriptionCancellationMessage,
} from '../../user-registration/breach/components/SubscriptionPackage/SubscriptionPackage.styled'
import { createLinkClickEvent } from '../../user-registration/breach/createLinkClickHandler'
import InfoMessageBox from './Components/InfoMessageBox'
import {
    StyledAnnualPackageContainer,
    StyledBannerImage,
    StyledBenefitsListWrappingComponent,
    StyledCallToActionArea,
    StyledDetailsCopyArea,
    StyledDetailsCopyAreaInner,
    UniOfferDetailsCopyAreaInner,
    StyledDeviceCompContainer,
    StyledIntroCopyArea,
    UniOfferIntroCopyArea,
    StyledIntroCopyAreaInner,
    StyledIntroDescription,
    StyledIntroTitle,
    StyledOriginalSubscriptionPrice,
    StyledPromoPageContainer,
    StyledPromoPageFooter,
    StyledSubscriptionPackagePrice,
    StyledSubscriptionPackagePriceContainer,
    StyledSubscriptionTerms,
    StyledTeacherNumberInput,
    StyledUniversityNumberInput,
    StyledVerifyButton,
    StyledViewAllPackagesButton,
    UniOfferBlueText,
    UniOfferImage,
    UniOfferImageContainer,
    UniOfferIntroDescription,
    UniOfferIntroSave,
    UniOfferIntroTextContainer,
    UniOfferIntroTitle,
    UniOfferTextStrikethrough,
    UniversityCallToActionTitle,
    StyledPromoStrapImage,
} from './RegionalsSwgPromoPage.styled'
import {
    getDefaultSwgCampaignValues,
    getSwgCampaignOfferValues,
    isSwgCampaignOfferValid,
    RegionalBenefitList,
    SwgCampaignOffer,
    SwgCampaignOfferValues,
} from '../campaigns'
import {
    defaultCreative,
    defaultDeal,
} from '../../user-registration/breach/components/BreachWallContent/BreachWallContentWrapper'
import SubscriptionPackage from '../../user-registration/breach/components/SubscriptionPackage/SubscriptionPackage'
import { getDefaultRrmeCampaignValues } from '../rrme-campaigns/RrmeDefaultOfferCampaign'
import { Logger } from '@west-australian-newspapers/typescript-log'

export interface SwgPageOptions {
    bannerImage?: {
        x1: string
        x2: string
    }
    modifier?: SwgCampaignOffer
    noIndex?: boolean
    heading?: string
    desiredRedirect?: string
}

export interface RegionalsSwgPromoPageProps {
    title?: string
    description: string
    image?: {
        x1: string
        x2: string
    }
    productId: string
    onEvent: (event: BreachEvent) => void
    publication: PublicationType
    sectionMeta: SectionMetaInfoDTO
    logger: Logger
    options?: SwgPageOptions
}

export function RegionalsSwgPromoPage(props: RegionalsSwgPromoPageProps) {
    const {
        description,
        image,
        onEvent,
        publication,
        title,
        options,
        sectionMeta,
        logger,
    } = props
    const { onSubscribeClick } = useAuthActions()
    const { bannerImage, modifier, desiredRedirect } = options || {}
    const campaignOfferFeature: string | undefined =
        useFeatureValue('swg-campaign-offer')
    const googleRrmeEnabled = useFeature('google-rrme-full-override')
    const uncheckedModifier = modifier || campaignOfferFeature
    const isOverridingCampaign =
        uncheckedModifier !== undefined && uncheckedModifier !== ''
    let swgCampaignValues: SwgCampaignOfferValues = googleRrmeEnabled
        ? getDefaultRrmeCampaignValues(logger)
        : getDefaultSwgCampaignValues()
    let campaignOffer: SwgCampaignOffer | undefined
    const uniOfferImage = require('./assets/2025-SWG-Uni-Student-Offer-Image.png')
    const theme = useTheme()
    const masthead =
        getAttribution(theme.kind, theme.section) || 'The West Australian'

    if (googleRrmeEnabled) {
        if (
            isOverridingCampaign &&
            isRrmeCampaignOfferValid(uncheckedModifier)
        ) {
            swgCampaignValues = getRrmeCampaignOfferValues(
                uncheckedModifier as RrmeCampaignOffer,
                logger,
            )
            campaignOffer = uncheckedModifier as RrmeCampaignOffer
        }
    } else {
        if (
            isOverridingCampaign &&
            isSwgCampaignOfferValid(uncheckedModifier)
        ) {
            swgCampaignValues = getSwgCampaignOfferValues(uncheckedModifier)
            campaignOffer = uncheckedModifier
        }
    }

    // Extract values from the campaign
    const {
        isDefault,
        deal,
        creative,
        TermsAndConditionsComponent,
        BenefitListComponent,
        promoContainerOverride,
    } = swgCampaignValues
    const thumbnailImage = swgCampaignValues.image || image
    const promoBackgroundImage = promoContainerOverride?.backgroundImage
    const promoStrapImage = promoContainerOverride?.promoStrap

    useUpdateBreachWallContext({
        publication,
        requiredAccessLevel: 'subscriber',
        breachCreativeName: swgCampaignValues.creative,
    })

    // handle whenever 'subscription options' is clicked.
    const onAllPackagesClicked = React.useCallback(() => {
        createLinkClickEvent(creative, deal, onEvent)()
        onSubscribeClick({
            callToAction: click_origin_breach_screen,
            breachScreenType: 'Premium - Inline',
        })
    }, [creative, deal, onEvent, onSubscribeClick])

    const StyledDetailsCopyAreaComponent =
        campaignOffer === 'university-offer'
            ? React.Fragment
            : StyledDetailsCopyArea
    const detailsCopyAreaArgs =
        campaignOffer != 'university-offer'
            ? { modifier: campaignOffer }
            : undefined
    // The package details area is different for the annual package
    const DetailsCopyArea =
        campaignOffer === 'annual-package'
            ? RegionalSwgBreachScreenComponent
            : RegionalSwgBreachSubscriptionPackageComponent

    return (
        <React.Fragment>
            <StyledPromoPageContainer>
                {/* If there's a banner image, eg. for uni-offer or teacher-offer */}
                {bannerImage && (
                    <StyledBannerImage
                        srcSet={`${bannerImage.x1} 1x, ${bannerImage.x2} 2x`}
                        src={bannerImage.x1}
                        alt="landing image of promo offer"
                    />
                )}

                {/* Intro Copy Area, the blue box at the top belongs here! */}
                {/* Uni Offer specific layout / styles */}
                {campaignOffer === UNIVERSITY_OFFER ? (
                    <UniOfferIntroCopyArea>
                        <UniOfferIntroTextContainer>
                            <UniOfferIntroTitle>
                                <div>
                                    Get{' '}
                                    <UniOfferBlueText>full</UniOfferBlueText>{' '}
                                    access.
                                </div>
                                <UniOfferTextStrikethrough>
                                    Pay full price.
                                </UniOfferTextStrikethrough>
                            </UniOfferIntroTitle>

                            <UniOfferIntroDescription>
                                Exclusive University Student Offer. <br />
                                Claim below using your WA student email and ID.
                            </UniOfferIntroDescription>
                            <UniOfferIntroSave>
                                Save over $400.
                            </UniOfferIntroSave>

                            <UniOfferImageContainer>
                                <UniOfferImage
                                    src={uniOfferImage}
                                    alt="Unlimited Digital Acess for $1 a month for 12 months"
                                />
                            </UniOfferImageContainer>
                        </UniOfferIntroTextContainer>
                    </UniOfferIntroCopyArea>
                ) : (
                    //  All other campaigns besides uni offer
                    <StyledIntroCopyArea
                        promoBackgroundImage={promoBackgroundImage}
                        promoStrapImage={promoStrapImage}
                    >
                        {promoStrapImage && (
                            <>
                                <StyledPromoStrapImage
                                    className="strap1"
                                    src={promoStrapImage}
                                    alt="promo strap image"
                                />
                                <StyledPromoStrapImage
                                    className="strap2"
                                    src={promoStrapImage}
                                    alt="promo strap image"
                                />
                                <StyledPromoStrapImage
                                    className="strap3"
                                    src={promoStrapImage}
                                    alt="promo strap image"
                                />
                            </>
                        )}
                        <StyledIntroCopyAreaInner>
                            <StyledIntroTitle>
                                {title ||
                                    'The News Delivered Directly to Your Device'}
                            </StyledIntroTitle>
                            <StyledIntroDescription>
                                {description}
                            </StyledIntroDescription>

                            <StyledDeviceCompContainer>
                                {thumbnailImage && (
                                    <img
                                        srcSet={`${thumbnailImage.x1}, ${thumbnailImage.x2} 2x`}
                                        src={thumbnailImage.x1}
                                        alt=""
                                    />
                                )}
                            </StyledDeviceCompContainer>
                        </StyledIntroCopyAreaInner>
                    </StyledIntroCopyArea>
                )}

                {/* Details Copy Area, breach screen package belongs here! */}
                <StyledDetailsCopyAreaComponent {...detailsCopyAreaArgs}>
                    <DetailsCopyArea
                        isDefault={isDefault}
                        campaignOffer={campaignOffer}
                        campaignValues={swgCampaignValues}
                        BenefitListComponent={BenefitListComponent}
                        productId={props.productId}
                        onEvent={onEvent}
                        masthead={masthead}
                        sectionMeta={sectionMeta}
                        onAllPackagesClicked={onAllPackagesClicked}
                        desiredRedirect={desiredRedirect}
                        log={logger}
                    />
                    {/* Footer to link to view 'All Packages' - We don't show this for the uni offer */}
                    {campaignOffer !== 'university-offer' && (
                        <StyledPromoPageFooter>
                            <StyledViewAllPackagesButton
                                onClick={() => {
                                    createLinkClickEvent(
                                        'regionals_swg_promo',
                                        'regionals_swg_promo',
                                        onEvent,
                                    )()
                                    onSubscribeClick({
                                        packagePath: '/',
                                    })
                                }}
                            >
                                View All Packages
                            </StyledViewAllPackagesButton>
                            <StyledHelpMessage>
                                Need help? Call us at{' '}
                                <StyledTextLink
                                    reverseColor={false}
                                    href="tel:1800 811 855"
                                >
                                    1800 811 855
                                </StyledTextLink>
                            </StyledHelpMessage>
                        </StyledPromoPageFooter>
                    )}
                    {TermsAndConditionsComponent}
                </StyledDetailsCopyAreaComponent>
            </StyledPromoPageContainer>
        </React.Fragment>
    )
}

interface SwgBreachSubscriptionProps {
    isDefault: boolean
    campaignOffer: SwgCampaignOffer | undefined
    campaignValues: SwgCampaignOfferValues
    BenefitListComponent: JSX.Element
    productId: string
    onEvent: (event: BreachEvent) => void
    masthead: string
    sectionMeta: SectionMetaInfoDTO
    onAllPackagesClicked: () => void
    log: Logger
    desiredRedirect?: string
}

/** Used for all offers except the annual package */
const RegionalSwgBreachSubscriptionPackageComponent = ({
    isDefault,
    campaignOffer,
    campaignValues,
    BenefitListComponent,
    productId,
    onEvent,
    masthead,
    sectionMeta,
    onAllPackagesClicked,
    desiredRedirect,
}: SwgBreachSubscriptionProps) => {
    const { sku, price: campaignPrice, InfoComponent } = campaignValues

    //Check verified mail params
    const buttonRef = useRef<HTMLButtonElement>(null)
    const scrollToRef = useRef<HTMLDivElement>(null)
    const hashedEvToken = new URLSearchParams(useLocation().search).get(
        'evtoken',
    )
    let flag = false
    hashedEvToken ? (flag = true) : (flag = false)

    // States to manage uni and teachers offer elements and actions
    const [isInputValid, setIsInputValid] = useState<boolean>(flag)
    const [teacherRegNumber, setTeacherRegNumber] = useState<string>('')
    const [isSidInputValid, setIsSidInputValid] = useState<boolean>(flag)
    const [isEmailInputValid, setIsEmailInputValid] = useState<boolean>(flag)
    const [isEmailVerified, setIsEmailVerified] = useState<boolean>(flag)
    const [uniSid, setUniSid] = useState<string>('')
    const [uniEmail, setUniEmail] = useState<string>('')
    const [universitySub, setUniversitySub] = useState({ sid: '', email: '' })
    const [verifyButtonAction, setVerifyButtonAction] = useState(flag)
    const [messageType, setMessageType] = useState('')
    const shouldToggleSwgBtn =
        campaignOffer === TEACHERS_OFFER || campaignOffer === UNIVERSITY_OFFER
    const { noAuthEmailVerifyDecryptKey } = useContext(ConfigurationContext)

    let offerMod: SwgMetaInfo | undefined = undefined

    if (desiredRedirect) {
        offerMod = { ...(offerMod ?? {}), desiredRedirect }
    }

    if (campaignOffer === TEACHERS_OFFER && teacherRegNumber) {
        offerMod = { ...(offerMod ?? {}), teacherRegNumber }
    }

    const uniName = 'curtin university'
    const defaultPrice = '$8 per week'

    if (campaignOffer === UNIVERSITY_OFFER) {
        offerMod = {
            ...(offerMod ?? {}),
            universitySubscriber: {
                universityName: uniName,
                email: universitySub?.email,
            },
        }
    }

    const displayMinCost =
        isDefault ||
        campaignOffer === 'black-friday' ||
        campaignOffer === 'footy-pack-2025'
    const hasPriceConditions = campaignPrice.billingTerms
    const InnerDetailsWrappingComponent =
        campaignOffer === 'university-offer'
            ? UniOfferDetailsCopyAreaInner
            : StyledDetailsCopyAreaInner

    const PackagePriceWrappingComponent = hasPriceConditions
        ? StyledSubscriptionPackagePriceContainer
        : React.Fragment
    const packagePriceWrappingArgs = hasPriceConditions
        ? { modifier: campaignOffer }
        : undefined
    // Apply the regional benefit list override, if it's a default campaign and not thewest
    const ExtractedBenefitListComponent =
        isDefault && productId !== 'thewest.com.au'
            ? RegionalBenefitList(productId)
            : BenefitListComponent

    // functions
    function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const val = e.currentTarget.value
        const isValid = val.length >= 5 && val.length <= 15 && /^\d+$/.test(val)
        setIsInputValid(isValid)
        isValid && setTeacherRegNumber(val)
        isValid && setIsEmailInputValid(true)
    }

    function onUniInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { id, value } = e.target
        if (id == 'uni-id') {
            const val = value
            setUniSid(val)
            const isSidValid =
                val.length >= 8 && val.length <= 8 && /^\d+$/.test(val)
            setIsSidInputValid(isSidValid)
            !isSidValid && setIsInputValid(false)
            isSidValid && setUniSid(val)
        }
        if (id == 'uni-email') {
            const email = value
            setUniEmail(email)

            const isValidStudentEmail =
                /^[a-zA-Z0-9.!#$%&*+/=?^_{|}~-]+@((student|postgrad)\.(curtin|murdoch|uwa)\.edu\.au|(our|my)\.(ecu|nd)\.edu\.au)$/

            const isTestStudentEmail =
                /^[a-zA-Z0-9.!#$%&*+/=?^_{|}~-]+@(swmdigital\.io|wanews\.com\.au)$/

            const isEmailValid =
                isValidStudentEmail.test(email) ||
                isTestStudentEmail.test(email)

            setIsEmailInputValid(isEmailValid)
            !isEmailValid && setIsInputValid(false)
            isEmailValid && setUniEmail(email)
        }
    }
    function verifyEmailButtonHandler() {
        const origin = `${location.origin}${location.pathname}`
        fetch(getEmailVerificationPath, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email: uniEmail,
                sid: uniSid,
                origin: origin,
            }),
        })
        setVerifyButtonAction(true)
    }
    function decryptData(token: string) {
        const decryptKey = noAuthEmailVerifyDecryptKey
        if (decryptKey) {
            const decodeToken = decodeURIComponent(token)
            const bytes = CryptoJS.AES.decrypt(decodeToken, decryptKey)
            const decrypted = bytes.toString(CryptoJS.enc.Utf8)
            const [email, sid, timestamp] = decrypted.split(':')
            const obj = { email, sid, timestamp }
            return obj
        }
    }
    function compareTimeToExpire(tokenTime: string) {
        //Time in minutes
        const currentTime = Date.now()
        const timeDiff = Math.floor(
            Math.abs(currentTime - parseInt(tokenTime)) / 60000,
        )
        if (timeDiff <= 240) {
            return false
        }
        return true
    }

    const googleRrmeEnabled = useFeature('google-rrme-full-override')
    const breachScreenContext = useContext(BreachScreenContext)
    const breachContext = breachScreenContext.context

    // useHook
    useSubscribeWithGoogle(
        buttonRef.current as HTMLButtonElement,
        sku,
        sectionMeta,
        breachContext,
        offerMod,
        googleRrmeEnabled,
    )

    // useEffect
    useEffect(() => {
        const price = EVERYDAY_DIGITAL
        const promoPrice = campaignPrice.weekly
        const discount = promoPrice !== price ? price - promoPrice : 0

        const viewSubscriptionItems: ViewSubscriptionItem[] = [
            {
                index: 0,
                deal_name: '',
                item_name: 'Everyday Digital',
                item_id: WEEKLY_DIGITAL_RATE_CODE,
                item_brand: masthead,
                component_path: 'subscribe/new',
                item_list_category: 'freemium',
                item_list_category2: 'Premium - Inline',
                item_list_name:
                    'TW_Article_Breach_WestBlue_Design_PhotoPackages',
                item_list_id: 'tw_article_breach_westblue_design_photopackages',
                price: price,
                promo_price: promoPrice,
                discount: discount,
                coupon: '',
                quantity: DEFAULT_CART_QUANTITY,
                currency: 'AUD',
            },
        ]

        onEvent({
            type: DataLayerEventName.viewSubscriptionItemListEvent,
            parentType: DataLayerEventName.viewItemListEvent,
            originator: 'swg_promo',
            payload: { items: viewSubscriptionItems },
        })

        if (hashedEvToken) {
            scrollToRef.current?.scrollIntoView({
                behavior: 'smooth',
            })

            const evData = decryptData(hashedEvToken)
            if (evData && evData.email && evData.sid && evData.timestamp) {
                const isTokenExpired = compareTimeToExpire(evData.timestamp)

                setUniEmail(evData.email)
                setUniSid(evData.sid)
                setUniversitySub({ sid: evData.sid, email: evData.email })

                if (isTokenExpired == true) {
                    setMessageType('TokenExpired')
                    setIsEmailVerified(false)
                    setVerifyButtonAction(false)
                }
            }
        }

        if (isSidInputValid && isEmailInputValid) {
            setUniversitySub({ sid: uniSid, email: uniEmail })
            setIsInputValid(true)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSidInputValid, isEmailInputValid, uniSid, uniEmail, hashedEvToken])

    return (
        <InnerDetailsWrappingComponent>
            {/* Purchase package (left side) */}
            <StyledCallToActionArea
                hideSwgBtn={
                    shouldToggleSwgBtn && (!isInputValid || !isEmailVerified)
                }
                ref={scrollToRef}
                modifier={campaignOffer}
            >
                {campaignOffer === 'university-offer' ? (
                    <UniversityCallToActionTitle>
                        Everyday Digital
                    </UniversityCallToActionTitle>
                ) : (
                    <h2>Everyday Digital</h2>
                )}
                {/* Original Price */}
                <StyledOriginalSubscriptionPrice>
                    {campaignPrice.originalPrice || ''}
                </StyledOriginalSubscriptionPrice>
                {/* Package Price */}
                <PackagePriceWrappingComponent {...packagePriceWrappingArgs}>
                    <StyledSubscriptionPackagePrice modifier={campaignOffer}>
                        {campaignPrice.promoPrice || defaultPrice}
                    </StyledSubscriptionPackagePrice>
                    {hasPriceConditions && (
                        <StyledSubscriptionTerms
                            terms={'billing'}
                            modifier={campaignOffer}
                        >
                            {campaignPrice.billingTerms}
                        </StyledSubscriptionTerms>
                    )}
                </PackagePriceWrappingComponent>
                {/* Teacher's offering requires TRBWA number to subscribe */}
                {campaignOffer === TEACHERS_OFFER && (
                    <StyledTeacherNumberInput isValid={isInputValid}>
                        <p>Teacher Registration Number:</p>
                        <input type="text" onChange={onInputChange} />
                        <small>Please enter a valid number.</small>
                    </StyledTeacherNumberInput>
                )}
                {/* University offering requires student number & email to subscribe */}
                {campaignOffer === UNIVERSITY_OFFER && (
                    <>
                        <StyledUniversityNumberInput isValid={isInputValid}>
                            <input
                                id="uni-id"
                                type="text"
                                placeholder="Student ID"
                                value={uniSid}
                                onChange={onUniInputChange}
                                disabled={isEmailVerified}
                            />
                            <input
                                id="uni-email"
                                type="email"
                                placeholder="Student email"
                                value={uniEmail}
                                onChange={onUniInputChange}
                                disabled={isEmailVerified}
                            />
                            <small>
                                Please enter a valid student id and student
                                email.
                            </small>
                        </StyledUniversityNumberInput>
                    </>
                )}

                {messageType === 'TokenExpired' && (
                    <InfoMessageBox
                        modifier={campaignOffer}
                        messageType="TokenExpired"
                    />
                )}
                {verifyButtonAction && messageType != 'TokenExpired' && (
                    <InfoMessageBox
                        modifier={campaignOffer}
                        isEmailVerified={isEmailVerified}
                    />
                )}

                {campaignOffer === UNIVERSITY_OFFER && !isEmailVerified && (
                    <StyledVerifyButton
                        isValid={isInputValid}
                        isSidValid={isSidInputValid}
                        isEmailValid={isEmailInputValid}
                        disabled={verifyButtonAction}
                        onClick={() => verifyEmailButtonHandler()}
                    >
                        {!verifyButtonAction ? 'Continue' : 'Email sent'}
                    </StyledVerifyButton>
                )}

                {/* Add the terms above the subscribe button, if provided */}
                {campaignPrice.priceTerms && (
                    <StyledSubscriptionTerms terms={'price'}>
                        {campaignPrice.priceTerms}
                    </StyledSubscriptionTerms>
                )}

                <StyledSubscribeWithGoogleButton
                    className={'regionalsswgpromogooglebutton'}
                    ref={buttonRef}
                    onClick={() => {
                        const price = EVERYDAY_DIGITAL
                        const promoPrice = campaignPrice.weekly
                        const discount =
                            price !== promoPrice ? price - promoPrice : 0
                        const truePrice = discount > 0 ? promoPrice : price

                        onEvent({
                            type: DataLayerEventName.addToCart,
                            originator: 'swg_promo',
                            payload: {
                                currency: 'AUD',
                                value: truePrice * DEFAULT_CART_QUANTITY,
                                items: [
                                    {
                                        item_id: 'WST_DIGI_DD',
                                        item_brand: masthead,
                                        item_name: 'Everyday Digital',
                                        price: price,
                                        promo_price: promoPrice,
                                        discount: discount,
                                        quantity: DEFAULT_CART_QUANTITY,
                                        coupon: '',
                                        item_variant: 'swg',
                                    },
                                ],
                            },
                        })
                    }}
                />
                {displayMinCost && (
                    <StyledSubscriptionCancellationMessage
                        packageOptions={{ theme: 'default' }}
                        breachOptions={{
                            style: 'default-breachwall-2024',
                        }}
                    >
                        Cancel anytime. Min cost ${campaignPrice.weekly * 4}.
                    </StyledSubscriptionCancellationMessage>
                )}

                {/* Now the text at the bottom of the package */}
                {InfoComponent(onAllPackagesClicked, productId)}
            </StyledCallToActionArea>
            {/* Benefits List (right side) */}
            <StyledBenefitsListWrappingComponent modifier={campaignOffer}>
                {ExtractedBenefitListComponent}
            </StyledBenefitsListWrappingComponent>
        </InnerDetailsWrappingComponent>
    )
}

/** Used for the Annual Package */
const RegionalSwgBreachScreenComponent = ({
    onEvent,
    masthead,
    onAllPackagesClicked,
    desiredRedirect,
    log,
}: SwgBreachSubscriptionProps) => {
    const googleRrmeEnabled = useFeature('google-rrme-full-override')
    const contentType = 'default-annual-package'

    const subscriptionPackages = googleRrmeEnabled
        ? rrmeCampaignOfferDetails(contentType, log)
        : packageOfferDetails(contentType)

    return (
        <React.Fragment>
            <StyledAnnualPackageContainer>
                {subscriptionPackages &&
                    subscriptionPackages.map(
                        (subscriptionPackageItem, index) => (
                            <SubscriptionPackage
                                key={index}
                                breachCreativeName={defaultCreative}
                                breachDeal={defaultDeal}
                                breachStyle={'default-breachwall-2024'}
                                onEvent={onEvent}
                                packageInfo={subscriptionPackageItem}
                                masthead={masthead}
                                isRegionalOverride={false}
                                isSwgPage={true}
                                desiredRedirect={desiredRedirect}
                            />
                        ),
                    )}
            </StyledAnnualPackageContainer>
            <LinkContext.Consumer>
                {() => (
                    <StyledTerms2022 breachType={'default-breachwall-2024'}>
                        *See other{' '}
                        <a href="#" onClick={onAllPackagesClicked}>
                            subscription options
                        </a>{' '}
                        and{' '}
                        <a
                            href={
                                'https://subscriber.thewest.com.au/subscription-terms'
                            }
                            target="_blank"
                        >
                            Terms &amp; Conditions
                        </a>
                    </StyledTerms2022>
                )}
            </LinkContext.Consumer>
        </React.Fragment>
    )
}

export default RegionalsSwgPromoPage
